import type { ReactNode } from 'react'

export interface InvoiceCompareBarProps {
  differing: number
  total: number
  expanded: boolean
  onToggle: () => void
  /** Omit where the values can't be edited (Invoice Prep). */
  onCopyAll?: () => void
  copyDisabled?: boolean
  copyTitle?: string
  children?: ReactNode
}

/**
 * One-line summary of how a service's invoice snapshot compares with its
 * customer. Quiet when everything matches; when something differs it offers a
 * toggle to reveal the per-field detail.
 */
export function InvoiceCompareBar(props: InvoiceCompareBarProps) {
  const { differing, total, expanded, onToggle, onCopyAll, copyDisabled, copyTitle, children } = props
  const same = differing === 0
  return (
    <div
      data-testid="invoice-compare-bar"
      className={`flex flex-wrap items-center justify-between gap-2 rounded px-2 py-1.5 text-xs ${
        same ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-900'
      }`}
    >
      <span>
        {same
          ? "Invoice details match the customer's current details."
          : `${differing} of ${total} invoice fields differ from the customer's current details.`}
      </span>
      <span className="flex items-center gap-2">
        {children}
        {!same ? (
          <button
            type="button"
            aria-expanded={expanded}
            onClick={onToggle}
            className="cursor-pointer rounded border border-amber-300 bg-white px-2 py-0.5 font-medium hover:bg-amber-100"
          >
            {expanded ? 'Hide differences' : 'Show differences'}
          </button>
        ) : null}
        {!same && onCopyAll ? (
          <button
            type="button"
            disabled={copyDisabled}
            title={copyTitle}
            onClick={onCopyAll}
            className="cursor-pointer rounded border border-amber-300 bg-white px-2 py-0.5 font-medium hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Copy all from customer
          </button>
        ) : null}
      </span>
    </div>
  )
}
